import React, { useState } from 'react';
import { useLang } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Send, Loader2, CheckCircle2, AlertCircle, Building2, Headphones } from 'lucide-react';
import axios from 'axios';

// In Dev: FastAPI backend; In Produktion (Plesk): /api/lead.php
const LEAD_ENDPOINT = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api/leads`
  : '/api/lead.php';

export default function EnterpriseForm() {
  const { t } = useLang();
  const [form, setForm] = useState({
    company_name: '', contact_person: '', email: '', phone: '',
    mailbox_count: '', zimbra_package: '', message: '',
  });
  const [status, setStatus] = useState(null);

  const update = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      await axios.post(LEAD_ENDPOINT, form);
      setStatus('success');
      setForm({ company_name: '', contact_person: '', email: '', phone: '', mailbox_count: '', zimbra_package: '', message: '' });
    } catch {
      setStatus('error');
    }
  };

  const inputCls = "w-full bg-white/[0.03] border border-white/10 text-white placeholder-zinc-600 py-3.5 px-4 rounded-xl focus:border-neon/50 focus:bg-white/[0.05] focus:outline-none focus:ring-1 focus:ring-neon/20 transition-all duration-300 text-sm font-light";

  return (
    <section id="enterprise" data-testid="enterprise-section" className="py-24 lg:py-32 bg-[#050505] relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-pink/8 rounded-full blur-[250px]" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-neon/5 rounded-full blur-[200px]" />
      <div className="absolute inset-0 grid-bg opacity-30" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left: Text */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-pink mb-4 bg-pink/10 border border-pink/20 rounded-full px-4 py-1.5">
              <Building2 className="w-3.5 h-3.5" />
              {t('enterprise.overline')}
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tight text-white mb-6">
              {t('enterprise.title')}
            </h2>
            <p className="text-base sm:text-lg text-zinc-400 font-light leading-relaxed mb-8">
              {t('enterprise.subtitle')}
            </p>

            {/* Image with gradient overlay */}
            <div className="relative rounded-2xl overflow-hidden group">
              <img
                src="/images/server-room.webp"
                alt="Secure server room in Germany"
                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 flex items-center gap-2 bg-black/60 backdrop-blur-xl rounded-full px-4 py-2">
                <Headphones className="w-4 h-4 text-cyan" />
                <span className="text-xs font-semibold text-zinc-300">24/7 Enterprise Support</span>
              </div>
            </div>
          </motion.div>

          {/* Right: Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <div className="bg-surface/80 backdrop-blur-xl border border-white/[0.06] rounded-3xl p-8 lg:p-10 relative overflow-hidden">
              {/* Subtle corner accent */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-pink/5 rounded-full blur-[60px]" />
              
              <form onSubmit={handleSubmit} className="space-y-5 relative z-10" data-testid="enterprise-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="company_name"
                    placeholder={t('enterprise.fields.company')}
                    value={form.company_name}
                    onChange={update('company_name')}
                    required
                    className={inputCls}
                    data-testid="form-company"
                  />
                  <input
                    type="text"
                    name="contact_person"
                    placeholder={t('enterprise.fields.contact')}
                    value={form.contact_person}
                    onChange={update('contact_person')}
                    required
                    className={inputCls}
                    data-testid="form-contact"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input
                    type="email"
                    name="email"
                    placeholder={t('enterprise.fields.email')}
                    value={form.email}
                    onChange={update('email')}
                    required
                    className={inputCls}
                    data-testid="form-email"
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder={t('enterprise.fields.phone')}
                    value={form.phone}
                    onChange={update('phone')}
                    className={inputCls}
                    data-testid="form-phone"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="mailbox_count"
                    placeholder={t('enterprise.fields.mailboxes')}
                    value={form.mailbox_count}
                    onChange={update('mailbox_count')}
                    className={inputCls}
                    data-testid="form-mailboxes"
                  />
                  <select
                    name="zimbra_package"
                    value={form.zimbra_package}
                    onChange={update('zimbra_package')}
                    className={`${inputCls} appearance-none cursor-pointer`}
                    data-testid="form-package"
                  >
                    <option value="" className="bg-surface text-zinc-500">{t('enterprise.fields.select_package')}</option>
                    <option value="basic" className="bg-surface">Zimbra Basic</option>
                    <option value="plus" className="bg-surface">Zimbra Plus</option>
                    <option value="pro" className="bg-surface">Zimbra Pro</option>
                  </select>
                </div>
                <textarea
                  name="message"
                  placeholder={t('enterprise.fields.message')}
                  value={form.message}
                  onChange={update('message')}
                  rows={4}
                  className={`${inputCls} resize-none`}
                  data-testid="form-message"
                />

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-pink text-white font-bold text-sm uppercase tracking-wide rounded-full px-10 py-4 hover:shadow-[0_0_40px_rgba(255,0,127,0.5)] transition-all duration-300 disabled:opacity-50 glow-pink"
                  data-testid="form-submit"
                >
                  {status === 'loading' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  )}
                  {t('enterprise.cta')}
                </button>

                {status === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 text-neon text-sm bg-neon/10 border border-neon/20 rounded-xl px-4 py-3"
                    data-testid="form-success"
                  >
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                    {t('enterprise.success')}
                  </motion.div>
                )}

                {status === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3"
                    data-testid="form-error"
                  >
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {t('enterprise.error')}
                  </motion.div>
                )}
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
